var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from 'axios';
import camelCase from 'lodash/camelCase';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import transform from 'lodash/transform';
import qs from 'query-string';
import { featureFlagClientInit, FeatureFlagUserBuilder, featureFlagUserIdentify, } from '@raisin/feature-flag-react-client';
import { getAccessToken, getBACNumberFromJWT, isCSR, isCustomerLoggedIn } from '../utils';
import Cache from './cache';
import { BASE_PATH, CLIENT_CACHE_KEY, COM_USERCENTRIC_ID, COUNTRIES_ISO_3, CUSTOMER_MOCK, CustomerStatus, CustomerType, DEFAULT_FF_BUILDER_PARAMS, DEFAULT_LOCALE, DOMAIN_FOR_LOCALE, DOMAIN_LOCALE, DOMAIN_LOCALE_SUFFIX, Environment, ENVIRONMENT_DOMAINS, EnvironmentTypes, FORMATTED_LOCALE, HttpMethod, HttpResponseMockType, LAUNCH_DARKLY_CLIENT_SIDE_IDS, LD_CLIENT_CACHE_KEY, LOCALE_USERCENTRIC_ID, MAIL_CONSENTS_MOCK, MICROSERVICES, REGISTRATION_BASE_PATH, Service, SITE_URL, UNIFIED_DOMAIN_LOCALES, WJIS_BASE_PATH, WJIS_BASE_PATH_LOCALE_SUFFIX, ZENDESK_SUBDOMAINS, } from './constants';
export const checkHost = (host) => { var _a; return isCSR() && ((_a = window.location.host) === null || _a === void 0 ? void 0 : _a.includes(host)); };
export const isLocalhost = () => checkHost('localhost');
export const getRegionFromHost = () => {
    var _a, _b, _c;
    if (isCSR() && isLocalhost()) {
        return ((_a = process.env.NEXT_PUBLIC_CP_REGION) === null || _a === void 0 ? void 0 : _a.toUpperCase()) || COUNTRIES_ISO_3.DEU;
    }
    return isCSR() && (((_c = (_b = window === null || window === void 0 ? void 0 : window.location) === null || _b === void 0 ? void 0 : _b.hostname) === null || _c === void 0 ? void 0 : _c.endsWith('.co.uk')) || checkHost('gbr-development'))
        ? COUNTRIES_ISO_3.GBR
        : COUNTRIES_ISO_3.DEU;
};
export const getNamespaceName = () => {
    if (isCSR()) {
        const name = /bk-(deu|gbr)-(.*)\.raisin-dev\.network/.exec(window.location.host);
        return name ? name[2] : EnvironmentTypes.DEVELOPMENT;
    }
    return EnvironmentTypes.DEVELOPMENT;
};
export const getEnvironment = () => {
    if (checkHost(EnvironmentTypes.STAGING))
        return EnvironmentTypes.STAGING;
    if (checkHost(EnvironmentTypes.ONBOARDING))
        return EnvironmentTypes.ONBOARDING;
    if (checkHost(EnvironmentTypes.PRODUCTION))
        return EnvironmentTypes.PRODUCTION;
    if (checkHost('dev-'))
        return EnvironmentTypes.DEVELOPMENT;
    if (checkHost('raisin-dev.network'))
        return getNamespaceName();
    if (checkHost('localhost'))
        return EnvironmentTypes.LOCAL;
    return EnvironmentTypes.PRODUCTION;
};
export const getLaunchDarklyClientSideId = () => {
    const namespace = getEnvironment();
    return LAUNCH_DARKLY_CLIENT_SIDE_IDS[namespace] || LAUNCH_DARKLY_CLIENT_SIDE_IDS.development;
};
export const buildFeatureFlagUser = ({ customerId, investorCountry, investorStatus, investorType, isAlphaGroup, isBetaGroup, distributor, }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        return new FeatureFlagUserBuilder(customerId)
            .investorType(investorType)
            .investorStatus(investorStatus)
            .distributor(distributor)
            .investorDomain(investorCountry)
            .country(investorCountry)
            .alphaInvestor(isAlphaGroup)
            .betaInvestor(isBetaGroup)
            .investorRegion(getRegionFromHost())
            .build();
    }
    catch (error) {
        throw new Error();
    }
});
export const shouldEnableUnifiedDomain = (locale) => {
    var _a, _b, _c, _d;
    if (UNIFIED_DOMAIN_LOCALES.includes(locale.toLowerCase()))
        return true;
    if (typeof window === 'undefined') {
        // rely on environment variable for ssg pages
        const supportedLocales = process.env.NEXT_PUBLIC_SUPPORTED_UNIFIED_DOMAIN_LOCALES ||
            process.env.REACT_APP_SUPPORTED_UNIFIED_DOMAIN_LOCALES ||
            '';
        return supportedLocales.toLowerCase().split(',').includes(locale.toLowerCase());
    }
    const isCOM = (_b = (_a = window.location) === null || _a === void 0 ? void 0 : _a.hostname) === null || _b === void 0 ? void 0 : _b.endsWith('.com');
    const localePath = (_d = (_c = window.location) === null || _c === void 0 ? void 0 : _c.pathname) === null || _d === void 0 ? void 0 : _d.split('/')[1];
    return isCOM && localePath.toLocaleLowerCase() === locale.toLowerCase();
};
export const getDomainConfig = (locale = 'en') => {
    const formattedLocale = FORMATTED_LOCALE[locale.toLowerCase()] || FORMATTED_LOCALE.en;
    // eslint-disable-next-line no-useless-escape
    const brokerRegistrationPath = `${REGISTRATION_BASE_PATH[formattedLocale]}\*\*\*\*\*`;
    const defaultBrokerRegistrationPath = '/kooperationspartner/register/?regflow=broker&product=%s';
    if (shouldEnableUnifiedDomain(formattedLocale)) {
        //fi,fr,pl
        return {
            siteUrl: `https://www.raisin.com/${formattedLocale.toLowerCase()}`,
            domainLocaleSuffix: DOMAIN_LOCALE_SUFFIX[formattedLocale],
            integrationServiceDomainName: 'api2',
            integrationServicePath: `raisin.com/wjis/${WJIS_BASE_PATH_LOCALE_SUFFIX[formattedLocale]}/v1`,
            basePath: BASE_PATH[formattedLocale],
            registrationPath: REGISTRATION_BASE_PATH[formattedLocale],
            domain: `raisin.com/${locale.toLowerCase()}`,
            userCentricId: COM_USERCENTRIC_ID,
            brokerRegistrationPath: DOMAIN_LOCALE_SUFFIX[formattedLocale]
                ? brokerRegistrationPath
                : defaultBrokerRegistrationPath,
        };
    }
    return {
        siteUrl: SITE_URL[formattedLocale],
        domainLocaleSuffix: '',
        integrationServiceDomainName: 'www',
        integrationServicePath: WJIS_BASE_PATH[formattedLocale],
        basePath: '',
        registrationPath: '/register/',
        domain: DOMAIN_FOR_LOCALE[locale],
        userCentricId: LOCALE_USERCENTRIC_ID[locale],
        brokerRegistrationPath: defaultBrokerRegistrationPath,
    };
};
const getIntegrationServiceUrl = (version = 'v1') => {
    var _a, _b;
    const hostEnvironment = ENVIRONMENT_DOMAINS[getEnvironment().toLowerCase()];
    if (version === 'v2')
        return `https://api2.${hostEnvironment}raisin.com/wjis/v2`;
    getEnvironment() === EnvironmentTypes.LOCAL && import('dotenv/config');
    const developmentLocale = process.env.REACT_APP_LOCALE || process.env.APP_LOCALE || 'en';
    const normalizedHostname = isCSR()
        ? (_a = window.location.hostname) === null || _a === void 0 ? void 0 : _a.replace(`www.${hostEnvironment}`, '')
        : '';
    const noneDevLocale = DOMAIN_LOCALE[normalizedHostname] || (isCSR() ? (_b = window.location.pathname) === null || _b === void 0 ? void 0 : _b.split('/')[1] : '');
    const locale = getEnvironment() === EnvironmentTypes.LOCAL || noneDevLocale === ''
        ? developmentLocale
        : noneDevLocale;
    const domainConfig = getDomainConfig(locale);
    return domainConfig
        ? `https://${domainConfig.integrationServiceDomainName}.${hostEnvironment}${domainConfig.integrationServicePath}`
        : '';
};
const getOBSServiceBaseURL = () => isCSR() ? `${window.location.origin}/savingglobal/rest/open_api/v2` : '';
export const getZendeskServiceBaseUrl = (locale) => `https://${ZENDESK_SUBDOMAINS[locale]}.zendesk.com`;
export const getPensionBaseUrl = () => getEnvironment() === EnvironmentTypes.PRODUCTION
    ? 'https://www.weltsparen.de/pension/v1'
    : 'https://www.staging-weltsparen.de/pension/v1';
const getMicroserviceNamespaceBaseUrl = (name, region) => ({
    [Service.KEYCLOAK]: `https://auth-${region}-${name}.raisin-dev.network/auth/realms/global`,
    [Service.CAS]: `https://customer-administration-service-${region}-${name}.raisin-dev.network/v1/`,
    [Service.TAMS]: `https://tams-${region}-${name}.raisin-dev.network/v1/`,
    [Service.CPMS]: `https://cpms-${name}-${region}.raisin-dev.network/public/v1/`,
    [Service.KCI]: `https://kyc-connector-idnow-${region}-${name}.raisin-dev.network/api/v1/`,
    [Service.DDS]: `https://document-depot-${region}-${name}.raisin-dev.network/api/v2/`,
    [Service.CPS]: `https://investor-postbox-service-${region}-${name}.raisin-dev.network/api/v1`,
    [Service.KSS]: `https://kyc-status-service-${region}-${name}.raisin-dev.network/api/v1`,
    [Service.CTS]: `https://cts-${region}-${name}.raisin-dev.network/v1`,
});
const getMicroserviceBaseURL = (service) => {
    var _a, _b, _c, _d;
    const environment = (_a = getEnvironment()) === null || _a === void 0 ? void 0 : _a.toLowerCase();
    const region = (_b = getRegionFromHost()) === null || _b === void 0 ? void 0 : _b.toLowerCase();
    const microservices = MICROSERVICES;
    const baseURL = (_d = (_c = microservices === null || microservices === void 0 ? void 0 : microservices[service]) === null || _c === void 0 ? void 0 : _c[region]) === null || _d === void 0 ? void 0 : _d[environment];
    if (baseURL)
        return baseURL;
    const namespaceUrl = getMicroserviceNamespaceBaseUrl(environment, region || 'deu')[service];
    if (environment && namespaceUrl)
        return namespaceUrl;
    return `https://internal-api.weltsparen.de/${service}/api/v1`;
};
export const getServiceBaseURL = (service, locale, version) => {
    switch (service) {
        case Service.IS:
        case Service.WJIS:
            return getIntegrationServiceUrl(version);
        case Service.OBS:
            return getOBSServiceBaseURL();
        case Service.Zendesk:
            return getZendeskServiceBaseUrl(locale || DEFAULT_LOCALE);
        case Service.PENSION:
            return getPensionBaseUrl();
        default:
            return getMicroserviceBaseURL(service);
    }
};
const IS_TEST_ENV = process.env.NODE_ENV === 'test';
const IS_MOCK_ENABLED = process.env.NEXT_PUBLIC_ENVIRONMENT_ENABLE_BACKEND_MOCK === 'true';
const shouldMockResponse = (mock) => {
    var _a;
    if (!mock || mock.disabled || !(IS_MOCK_ENABLED || IS_TEST_ENV))
        return false;
    return ((_a = mock.environments) !== null && _a !== void 0 ? _a : [Environment.Local]).includes(getEnvironment());
};
export const sleep = (time = 1000) => __awaiter(void 0, void 0, void 0, function* () { return new Promise((res) => setTimeout(res, time)); });
export const getLdClientWithoutCustomerTarget = () => __awaiter(void 0, void 0, void 0, function* () {
    return featureFlagClientInit(getLaunchDarklyClientSideId(), yield buildFeatureFlagUser(DEFAULT_FF_BUILDER_PARAMS));
});
let shouldUseIstioGatewayForCPS = false;
let isLdClientInitialized = false;
// Internal function to initialize the LaunchDarkly client and set flags
const initializeLdClient = () => __awaiter(void 0, void 0, void 0, function* () {
    if (isLdClientInitialized || process.env.NODE_ENV === 'test') {
        return; // Prevent re-initialization
    }
    if (isCSR()) {
        const ldClient = yield getLdClientWithoutCustomerTarget();
        shouldUseIstioGatewayForCPS = ldClient === null || ldClient === void 0 ? void 0 : ldClient.variation('baseCallingPostboxV2EndpointOnCP', false);
        isLdClientInitialized = true;
    }
});
// Function to ensure initialization has been completed before proceeding
const ensureInitialization = () => __awaiter(void 0, void 0, void 0, function* () {
    if (!isLdClientInitialized) {
        yield initializeLdClient();
    }
});
const buildConfigHeaders = ({ headers, overrideAuthorizationToken = false }) => {
    const token = getAccessToken();
    const builtHeaders = Object.assign({ 'Content-Type': 'application/json' }, headers);
    if (token && !overrideAuthorizationToken) {
        builtHeaders['Authorization'] = `Bearer ${token}`;
    }
    return builtHeaders;
};
const mapPathParamsToURL = ({ url, pathParams }) => {
    if (!(url && pathParams)) {
        return url;
    }
    let result = url;
    Object.entries(pathParams || {}).forEach(([k, v]) => {
        result = result.replace(`:${k}`, encodeURIComponent(v));
    });
    return result;
};
export const toCamelCase = (obj) => {
    const result = transform(obj, (acc, value, key, target) => {
        const camelKey = isArray(target) ? key : camelCase(key);
        acc[camelKey] = isObject(value) ? toCamelCase(value) : value;
    });
    return result;
};
export const TechnicalErrorCode = 'technicalError';
export const createClient = (baseURL, timeout, isCaseMapperEnabled) => {
    const api = axios.create({ baseURL, timeout });
    api.interceptors.request.use((config) => {
        config.headers = buildConfigHeaders(config);
        config.url = mapPathParamsToURL(config);
        return config;
    }, (error) => Promise.reject(error));
    api.interceptors.response.use((res) => {
        const response = {
            data: isCaseMapperEnabled ? toCamelCase(res.data) : res.data,
            headers: res.headers,
            status: res.status,
        };
        return response;
    }, (err) => {
        let appError = null;
        if (err === null || err === void 0 ? void 0 : err.response) {
            if (Array.isArray(err === null || err === void 0 ? void 0 : err.response.data)) {
                appError = {
                    status: err === null || err === void 0 ? void 0 : err.response.status,
                    error: err === null || err === void 0 ? void 0 : err.response.data[0].code,
                    message: err === null || err === void 0 ? void 0 : err.response.data[0].message,
                    errorList: err === null || err === void 0 ? void 0 : err.response.data,
                };
            }
            else {
                /**
                 * The request was made and the server responded with a
                 * status code that falls out of the range of 2xx
                 */
                // eslint-disable-next-line @typescript-eslint/naming-convention
                const { status, data: { frontend_error_code: errorCode, error, message, code }, } = err.response;
                appError = {
                    status,
                    message,
                    error,
                    code: errorCode || code,
                };
            }
        }
        else if (!err || typeof err.response === 'undefined') {
            /**
             * The request was made but no response was received, `error.request`
             * is an instance of XMLHttpRequest in the browser and an instance
             * of http.ClientRequest in Node.js
             */
            appError = {
                status: 400,
                error: 'Invalid Request Payload',
                message: 'A technical error occurred.',
                code: TechnicalErrorCode,
            };
        }
        return Promise.reject(appError);
    });
    return api;
};
export const REQUEST_TIMEOUT = 'requestTimeout';
export const getServiceTimeout = (service) => { var _a; return (_a = MICROSERVICES[service]) === null || _a === void 0 ? void 0 : _a[REQUEST_TIMEOUT]; };
const CLIENTS = Object.values(Service).reduce((acc, service) => {
    acc[service] = createClient(getServiceBaseURL(service), getServiceTimeout(service));
    return acc;
}, {});
export const createHttpAction = (request, mock) => __awaiter(void 0, void 0, void 0, function* () {
    // Handle mocks first
    if (shouldMockResponse(mock)) {
        const { error, type, delay, data, headers, status } = mock || {};
        if (!IS_TEST_ENV)
            yield sleep(delay);
        if (type === HttpResponseMockType.REJECT) {
            return Promise.reject(error);
        }
        else {
            return Promise.resolve({ data, headers, status });
        }
    }
    // Ensure feature flags are initialized before proceeding
    yield ensureInitialization();
    const { service, url, method, data, settings } = request;
    if (!(service && url)) {
        throw new Error('Missing required parameter when calling "createHttpAction"');
    }
    const action = method !== null && method !== void 0 ? method : HttpMethod.GET;
    let requestSettings = settings || {};
    if (service === Service.CPS && shouldUseIstioGatewayForCPS) {
        const cpsBaseUrl = getServiceBaseURL(service);
        requestSettings.baseURL = cpsBaseUrl.replace(/^https:\/\/(api)\./, 'https://api2.');
    }
    // Handle HTTP methods
    if ([HttpMethod.GET, HttpMethod.DELETE].includes(action)) {
        return CLIENTS[service][action.toLowerCase()](url, requestSettings);
    }
    return CLIENTS[service][action.toLowerCase()](url, data, requestSettings);
});
export const getCustomerMailConsents = () => __awaiter(void 0, void 0, void 0, function* () {
    const req = {
        service: Service.IS,
        url: `/mail/consents`,
        method: HttpMethod.GET,
        settings: {
            withCredentials: true,
        },
    };
    const mock = { data: MAIL_CONSENTS_MOCK };
    return createHttpAction(req, mock);
});
export const getFeatureFlagConsentsData = () => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c;
    let consents;
    try {
        const consentsResponse = yield getCustomerMailConsents();
        consents = (_a = consentsResponse === null || consentsResponse === void 0 ? void 0 : consentsResponse.data) === null || _a === void 0 ? void 0 : _a.consents;
    }
    catch (_d) {
        // eslint-disable-next-line no-console
        console.warn('Failed to load "consents" data for feature-flag params!');
    }
    return {
        isAlphaGroup: ((_b = consents === null || consents === void 0 ? void 0 : consents.find((consent) => consent.id === 'alpha_program')) === null || _b === void 0 ? void 0 : _b.valid) || false,
        isBetaGroup: ((_c = consents === null || consents === void 0 ? void 0 : consents.find((consent) => consent.id === 'beta_program')) === null || _c === void 0 ? void 0 : _c.valid) || false,
    };
});
export const getCustomerForLDClient = (customerId) => __awaiter(void 0, void 0, void 0, function* () {
    const req = {
        service: Service.CAS,
        url: '/customers/:customerId',
        settings: {
            pathParams: { customerId },
        },
    };
    const mock = {
        data: CUSTOMER_MOCK,
    };
    return createHttpAction(req, mock);
});
export const isBusinessCustomer = ({ type }) => type === CustomerType.BUSINESS;
export const isCustomerPendingActivation = ({ status }) => [CustomerStatus.Registered, CustomerStatus.Identified, CustomerStatus.PendingAcceptance].includes(status);
export const getLDCustomerProperties = (customer) => {
    var _a;
    if (!customer) {
        return {};
    }
    return {
        customerId: customer === null || customer === void 0 ? void 0 : customer.id,
        investorCountry: ((_a = customer === null || customer === void 0 ? void 0 : customer.address) === null || _a === void 0 ? void 0 : _a.countryCode) || '',
        investorType: isBusinessCustomer(customer) ? 'business' : 'retail',
        investorStatus: isCustomerPendingActivation(customer) ? 'non-exported' : 'exported',
        distributor: customer.distributorId,
    };
};
export const getFeatureFlagUserData = (customerId) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const customerResponse = yield getCustomerForLDClient(customerId);
        return getLDCustomerProperties(customerResponse === null || customerResponse === void 0 ? void 0 : customerResponse.data);
    }
    catch (_e) {
        // eslint-disable-next-line no-console
        console.warn('Failed to load "customer" data for feature-flag params!');
        return {};
    }
});
export const getFeatureFlagBuilderParams = () => __awaiter(void 0, void 0, void 0, function* () {
    if (!isCustomerLoggedIn())
        return DEFAULT_FF_BUILDER_PARAMS;
    const customerId = getBACNumberFromJWT() || '';
    const consentsParams = (yield getFeatureFlagConsentsData()) || {};
    const customerParams = yield getFeatureFlagUserData(customerId);
    return Object.assign(Object.assign(Object.assign({}, DEFAULT_FF_BUILDER_PARAMS), consentsParams), customerParams);
});
export const getFeatureFlagClient = () => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const cachedClient = yield Cache.get(LD_CLIENT_CACHE_KEY);
        if (cachedClient) {
            console.info('Using cached LaunchDarkly client');
            return cachedClient;
        }
        const options = { streaming: true };
        const clientSideId = getLaunchDarklyClientSideId();
        const user = yield buildFeatureFlagUser(yield getFeatureFlagBuilderParams());
        const client = yield featureFlagClientInit(clientSideId, user, options);
        if (client) {
            Cache.set(LD_CLIENT_CACHE_KEY, client, 5 * 60 * 1000); // 5 minutes
            yield featureFlagUserIdentify(client, user);
            return client;
        }
        throw new Error('Failed to initialize LaunchDarkly client.');
    }
    catch (error) {
        console.warn('Failed initializing LaunchDarkly client.', error);
        return undefined;
    }
});
export const fetchCustomer = (customerId) => __awaiter(void 0, void 0, void 0, function* () {
    const bacNumber = customerId || getBACNumberFromJWT();
    if (!bacNumber) {
        return { data: null, error: new Error('No customer ID or BAC number provided') };
    }
    const request = {
        service: Service.CAS,
        url: '/customers/:bacNumber',
        settings: {
            params: { embed: ['all-persons', 'reference-accounts'] },
            pathParams: { bacNumber },
            paramsSerializer: qs.stringify,
        },
    };
    try {
        const { data } = yield createHttpAction(request, {
            data: CUSTOMER_MOCK,
        });
        return { data: data !== null && data !== void 0 ? data : null, error: null };
    }
    catch (error) {
        return {
            data: null,
            error: error instanceof Error ? error : new Error('Unknown error occurred'),
        };
    }
});
export const createRaisinFrontendUser = () => __awaiter(void 0, void 0, void 0, function* () {
    var _f, _g, _h, _j, _k, _l, _m;
    try {
        const cachedClient = yield Cache.get(CLIENT_CACHE_KEY);
        if (cachedClient) {
            console.info('Using cached LaunchDarkly client');
            return cachedClient;
        }
        const customer = yield fetchCustomer();
        const isCompanyCustomer = ((_f = customer === null || customer === void 0 ? void 0 : customer.data) === null || _f === void 0 ? void 0 : _f.type) === 'BUSINESS';
        const isPendingActivation = ((_g = customer === null || customer === void 0 ? void 0 : customer.data) === null || _g === void 0 ? void 0 : _g.status) &&
            ['REGISTERED', 'IDENTIFIED', 'PENDING_SERVICING_BANK_ACCEPTANCE'].includes((_h = customer === null || customer === void 0 ? void 0 : customer.data) === null || _h === void 0 ? void 0 : _h.status);
        const hasCustomer = !!((_j = customer === null || customer === void 0 ? void 0 : customer.data) === null || _j === void 0 ? void 0 : _j.id);
        const type = isCompanyCustomer ? 'business' : 'retail';
        const status = isPendingActivation ? 'non-exported' : 'exported';
        const clientSideId = getLaunchDarklyClientSideId();
        const user = new FeatureFlagUserBuilder((_k = customer === null || customer === void 0 ? void 0 : customer.data) === null || _k === void 0 ? void 0 : _k.id)
            .investorRegion(getRegionFromHost())
            .investorDomain(((_l = customer === null || customer === void 0 ? void 0 : customer.data) === null || _l === void 0 ? void 0 : _l.address.countryCode) || '')
            .investorType(hasCustomer ? type : '')
            .investorStatus(hasCustomer ? status : '')
            .distributor(((_m = customer === null || customer === void 0 ? void 0 : customer.data) === null || _m === void 0 ? void 0 : _m.distributorId) || '')
            .build();
        const options = {
            streaming: true,
        };
        const client = yield featureFlagClientInit(clientSideId, user, options);
        if (client) {
            Cache.set(CLIENT_CACHE_KEY, client, 5 * 60 * 1000); // 5 minutes
            yield featureFlagUserIdentify(client, user);
            return client;
        }
        throw new Error('Failed to initialize LaunchDarkly client.');
    }
    catch (error) {
        console.warn('Failed initializing LaunchDarkly client.', error);
        return undefined;
    }
});
//# sourceMappingURL=utils.js.map